import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Tooltip,
} from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { ASSIST_VERBAL_TOOLTIP, PROMPTING_LEVEL_LABELS } from '../../../constants/GoalsConstants';

const PromptingFields = ({
  dataPoint,
  index,
  promptingType,
  existingPromptingType,
  handlePromptingChange,
  handleFieldChange,
  goalType,
  goalPromptingLevel,
  prompting,
  allGoalData,
}) => {
  // Helper function to render all tooltips in a formatted list
  const renderAllTooltips = () => {
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {ASSIST_VERBAL_TOOLTIP.map((item) => (
          <li key={item.key}>
            <strong>{item.label}</strong>: {item.tooltip}
          </li>
        ))}
      </ul>
    );
  };

  // Helper function to get tooltip content based on prompting level
  const getPromptingTooltip = (promptingLevel) => {
    // Map prompting level values to tooltip keys
    const levelToKeyMap = {
      'physical-full-total-assist': 'total_assist',
      'physical-full-maximal-assist': 'maximal_assist',
      'physical-partial-moderate': 'moderate_assist',
      'physical-partial-minimal': 'minimal_contact_assist',
      'verbal-minimal': 'minimal_verbal',
      'verbal-moderate': 'moderate_verbal',
      'verbal-maximum': 'maximum_verbal',
      'independent': 'independent',
    };

    // If no prompting level is selected, show all tooltips
    if (!promptingLevel) {
      return renderAllTooltips();
    }

    // If a specific prompting level is selected, show only that tooltip
    const key = levelToKeyMap[promptingLevel];
    if (!key) {
      // If the selected level doesn't have a tooltip mapping, show all
      return renderAllTooltips();
    }

    // Return the specific tooltip for the selected level
    const tooltipItem = ASSIST_VERBAL_TOOLTIP.find((item) => item.key === key);
    return tooltipItem ? tooltipItem.tooltip : renderAllTooltips();
  };

  const getDataPointPromptingLevel = () => {

    if (
      allGoalData &&
      dataPoint.currentGoal &&
      dataPoint.currentGoal !== 'main'
    ) {
      const subgoals =
        allGoalData?.HasSubgoal?.Subgoals ||
        allGoalData?.dataCollection?.HasSubgoal?.Subgoals ||
        [];

      // Try multiple matching strategies to locate the sub-goal definition
      let targetSubgoal =
        subgoals.find((sg) => sg.SubgoalNumber === dataPoint.currentGoal) ||
        subgoals.find(
          (sg) => parseInt(sg.SubgoalNumber) === parseInt(dataPoint.currentGoal)
        ) ||
        subgoals.find(
          (sg) => String(sg.SubgoalNumber) === String(dataPoint.currentGoal)
        );

      if (!targetSubgoal) {
        // Strategy: index based (if currentGoal is "1", pick subgoals[0])
        const goalIndex = parseInt(dataPoint.currentGoal) - 1;
        if (goalIndex >= 0 && goalIndex < subgoals.length) {
          targetSubgoal = subgoals[goalIndex];
        }
      }

      if (targetSubgoal?.PromptingLevel) {
        return targetSubgoal.PromptingLevel;
      }
    }

    // --- Main goal prompting level -----------------------------------------
    const mainPromptingLevel =
      allGoalData?.DataCollection?.measurementType?.PromptingLevel ||
      allGoalData?.dataCollection?.DataCollection?.measurementType?.PromptingLevel ||
      goalPromptingLevel;

    return mainPromptingLevel || '';
  };

  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  // Prompting Measurement Section
  const renderPromptingMeasurement = () => {
    if (goalType !== 'Prompting') return null;

    // Use the prompting level for the specific subgoal this data entry belongs to
    const rawLevel = getDataPointPromptingLevel();
    const promptingTooltip = getPromptingTooltip(rawLevel);
    
    // Get the prompting level label for the unit
    const promptingLevelLabel = rawLevel 
      ? (PROMPTING_LEVEL_LABELS[rawLevel] || rawLevel.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
      : '';

    return (
      <Paper 
        elevation={1}
        sx={{ 
          p: 2, 
          mb: 2, 
          backgroundColor: 'transparent',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TouchApp sx={{ color: '#6b7280', mr: 1, fontSize: 18 }} />
          <Typography 
            variant='body1' 
            sx={{ 
              color: '#1f2937',
              fontWeight: 400,
              fontSize: '1rem'
            }}
          >
            Prompting Measurement
          </Typography>
          <Tooltip title={promptingTooltip}>
            <InfoIcon
              sx={{
                fontSize: 'small',
                ml: 0.5,
                verticalAlign: 'middle',
                cursor: 'help',
                color: '#6b7280',
              }}
            />
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography 
            variant='body1'
            sx={{ 
              color: '#374151',
              fontWeight: 500
            }}
          >
            Prompting Measurement:
          </Typography>
          <TextField
            type='number'
            inputProps={{
              'aria-label': 'Prompting measurement',
              inputMode: 'numeric',
              onWheel: preventNumberScroll,
            }}
            margin='dense'
            size='small'
            value={dataPoint.value || ''}
            sx={{ width: '15%' }}
            onChange={(e) =>
              handleFieldChange(index, 'value', e.target.value)
            }
          />
          {promptingLevelLabel && (
            <Typography variant='body1' sx={{ color: '#374151' }}>
              {promptingLevelLabel}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  return <>{renderPromptingMeasurement()}</>;
};

export default PromptingFields;
